import { Logger } from "../utils/Logger";
import { UserManager } from "../shared/UserManager";
import { GtmDataSyncLib } from "../shared/GtmDataSyncLib";
import { UserIdExchange } from "../shared/UserIdExchange";
import { container, inject, injectable, singleton } from "tsyringe";
import { SiteRules } from "../shared/SiteRules";
import { invokeWhenProfileSetup } from "../cdp/BlueConicUtils";
import { GtmEventNames } from "../gtm/GtmEventNames";
import { getOrCreateUserAdapter, pushEventToDataLayer } from "../gtm/GtmUtils";
import { DataSyncLibEventNames } from "../shared/DataSyncLibEventNames";
import { isCurrentSiteExternal, isCurrentSiteRci } from "./DotCaPageChecks";
import { ReadJsApiClient } from "../caspian/readJs/ReadJsApiClient";
import { StreamMediaType, validateStreamMediaType } from "../shared/StreamMediaType";
import { UieClient } from "../cbcUie/UieClient";
import { DotCaCdpClient } from "./DotCaCdpClient";
import { TimelineEventIds } from "../cdp/TimelineEventsId";
import { GeoIpService } from "../shared/geoLocation/GeoIpService";
import { DotCaEngagementService } from "./DotCaEngagementService";

@singleton()
@injectable()
export class DotCaLib extends GtmDataSyncLib {
  constructor(
    @inject(DotCaCdpClient) public cdpClient: DotCaCdpClient,
    @inject(UserIdExchange) private userIdExchange: UserIdExchange,
    @inject(UserManager) private userManager: UserManager,
    @inject(SiteRules) private siteRules: SiteRules,
    @inject(ReadJsApiClient) private readJsApiClient: ReadJsApiClient,
    @inject(GeoIpService) private geoIpService: GeoIpService,
    @inject(DotCaEngagementService) private engagementService: DotCaEngagementService,
  ) {
    super();
  }

  /**
   * We are dependent on multiple triggers in GTM:
   * - aaTrackPageView: a page view occurred. Occurs on every page view.
   * - iFrameView: not clear when it happens... but equivalent to a page view.
   * - userInfo: the information about the current user is available. This event occurs
   *             only once on the first page load. Page views do not trigger this event.
   * We can't do anything until the userInfo event is triggered.
   * Therefore, this flag indicate whether the userInfo event occurred and user information is available.
   */
  private _userInitialized = false;

  /** Since the user initialization process is asynchronous, this flag is used
   * to prevent the process from running more than once.
   */
  private _userInitializationStarted = false;

  public async setupCurrentUser(): Promise<void> {
    Logger.verbose("setupCurrentUser");
    this.validateStorageAccess();
    if (!this._userInitializationStarted) {
      this._userInitializationStarted = true;
      if (!this.siteRules.isGTMLibraryEnabled) {
        Logger.verbose("Library disabled, aborting.");
        return;
      }
      if (CDPEnabled) {
        await this.cdpClient.initialize();
        invokeWhenProfileSetup(async () => {
          await this.userManager.initDataLayerUserAndUIE();
          this._userInitialized = true;
          window.dispatchEvent(new Event(DataSyncLibEventNames.UserInitialized));
        });
      } else {
        // HOTFIX to generate RCVisitorId if anonymous, if CDP is disabled long term, below code should be moved in UserManager as a function
        const isAnonymous = container.resolve<SiteRules>(SiteRules).isAnonymousUser;
        if (isAnonymous) {
          const dataLayerUser = getOrCreateUserAdapter();
          const uieClient = container.resolve<UieClient>(UieClient);
          dataLayerUser.rc_visitor_id = await uieClient.handleRcVisitorId();
          dataLayerQueue.push({
            user: dataLayerUser,
          });
        }
        await this.userManager.initDataLayerUserAndUIE();
        this._userInitialized = true;
        window.dispatchEvent(new Event(DataSyncLibEventNames.UserInitialized));
      }
    }
  }

  public async handlePageView(): Promise<void> {
    Logger.verbose("handlePageView"); // This message is used in PW tests.
    if (!this._userInitializationStarted && (isCurrentSiteRci() || isCurrentSiteExternal())) {
      // There is no login on RCI or on external sites.
      // The current user will always be anonymous and the userInfo event won't be triggered.
      await this.setupCurrentUser();
    }

    this.invokeWhenUserInitialized(async () => {
      // Load the postal code in the dataLayer for Lotame in the GTM. Tag lotame_logic_sendData.
      await this.geoIpService.fetchAndStoreUserPostalCode();

      // To maintain backward compatibility with existing GTM tags.
      // Check the GTM to validate if these triggers are still in use.
      pushEventToDataLayer(GtmEventNames.SetUserInfoDone);
      pushEventToDataLayer(GtmEventNames.UserIdExchangeDone);

      if (!this.siteRules.isGTMLibraryEnabled) {
        Logger.verbose("Library disabled, aborting.");
        return;
      }
      if (!this.siteRules.isTrackingEnabled) {
        Logger.verbose("Tracking disabled: aborting handlePageView");
        return;
      }
      this.engagementService.handlePageView();

      if (CDPEnabled) {
        await this.cdpClient.handlePageView();
      }
    });
  }

  public async handleReadEvent(): Promise<void> {
    Logger.verbose("handleReadEvent");
    if (!this.siteRules.isGTMLibraryEnabled) {
      Logger.verbose("Library disabled, Aborting.");
      return;
    }
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handlePageView");
      return;
    }

    // Immediately capture the articleId to send the event once the user is set up.
    const articleId = dataLayerHelper.get<string>("page.IdMedia");
    if (CDPEnabled) {
      this.cdpClient.sendRcRead();
      invokeWhenProfileSetup(async () => {
        await this.sendReadJsEventToApi(articleId);
      });
    } else {
      this.invokeWhenUserInitialized(async () => {
        await this.sendReadJsEventToApi(articleId);
      });
    }
  }

  public async handleContentStart(mediaType: StreamMediaType): Promise<void> {
    Logger.verbose("handleContentStart");
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleContentStart");
      return;
    }
    if (CDPEnabled && validateStreamMediaType(mediaType)) {
      await this.cdpClient.sendRcStream(mediaType);
    }
  }

  // Invoked by the GTM tag datasync_handle_morduBookmark
  // noinspection JSUnusedGlobalSymbols
  public handleMorduBookmark(): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleMorduBookmark");
      return;
    }
    if (CDPEnabled) {
      this.cdpClient.sendMorduTimelineEvent(TimelineEventIds.RcMorduBookmark);
    }
  }

  // Invoked by the GTM tag datasync_handle_morduScore
  // noinspection JSUnusedGlobalSymbols
  public handleMorduScore(): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleMorduScore");
      return;
    }
    if (CDPEnabled) {
      this.cdpClient.sendMorduTimelineEvent(TimelineEventIds.RcMorduScore);
    }
  }

  // Invoked by the GTM tag datasync_handle_actionPSI
  // noinspection JSUnusedGlobalSymbols
  public handleActionPSI(actionValue: string | undefined, actionCData: string | undefined): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleActionPSI");
      return;
    }

    if (CDPEnabled) {
      const variantMatch = actionCData?.match(/variant:([^|]+)/);
      const variantValue = variantMatch ? variantMatch[1] : "";
      // action.valeur defaults to "aucune valeur" if not set in the GTM variable
      const isActionValueNullOrDefault = !actionValue || actionValue === "aucune valeur";
      if (isActionValueNullOrDefault || !variantValue) {
        Logger.warn(`Missing actionValue or variantValue: ${actionValue} - ${variantValue}`);
      }
      this.cdpClient.sendActionPSITimeLineEvent(actionValue, variantValue);
    }
  }

  // Invoked by the GTM tag datasync_handle_morduComment
  // noinspection JSUnusedGlobalSymbols
  public handleMorduComment(): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleMorduComment");
      return;
    }
    if (CDPEnabled) {
      this.cdpClient.sendMorduTimelineEvent(TimelineEventIds.RcMorduComment);
    }
  }

  // Invoked by the GTM tag gtmRc_set_user_postalCode
  // noinspection JSUnusedGlobalSymbols
  public handleSetUserPostalCode(): void {
    if (!this.siteRules.isTrackingEnabled) {
      Logger.verbose("Tracking disabled: aborting handleSetUserPostalCode");
      return;
    }
    if (CDPEnabled) {
      this.cdpClient.setUserRegionalPostalCode();
    }
  }

  public handleConsentChanged(): Promise<void> {
    // Will be handled in a future phase of the CMP implementation
    throw "Not Implemented";
  }

  // Invoked by the GTM tag lotame_logic_audience
  public async setLotameIdInUie(lotameId: string): Promise<void> {
    await this.userIdExchange.setLotameIdInUie(lotameId);
  }

  private async sendReadJsEventToApi(articleId: string | undefined): Promise<void> {
    Logger.verbose("ArticleID:", articleId);
    await this.readJsApiClient.sendReadJsEvent(articleId);
  }

  private invokeWhenUserInitialized(callback: () => void): void {
    if (this._userInitialized) {
      callback();
    } else {
      window.addEventListener(DataSyncLibEventNames.UserInitialized, callback);
    }
  }
}
